<template>
  <a-modal
    v-if="visible"
    :visible="visible"
    width="400px"
    :destroy-on-close="true"
    :title="title"
    data-testid="shifts-add-shift-modal"
    @cancel="closeModal"
  >
    <shift-additional
      :shift="{ ...shiftForm, signature: shift?.shift?.signature, explanation: shift?.shift?.explanation }"
      :associate="shift.associate"
      v-if="!loadingShift"
    />

    <associate-create-modal
      :visible.sync="visibleCreateAssociateModal"
      data-testid="shifts-add-shift-create-associate-modal"
      @add="afterCreateAssociate"
    />

    <a-modal v-model="visibleQrModal" :footer="null" :destroy-on-close="true" data-testid="shifts-add-shift-qr-modal">
      <div class="flex justify-content-center">
        <qr-code-generator :link="baseUrl" />
      </div>
    </a-modal>

    <shift-confirmation
      v-if="associate && associate.payment_info"
      :visible.sync="visibleConfirmationModal"
      :added-authorizations="authorizationDiff.added"
      :removed-authorizations="authorizationDiff.removed"
      :existing-authorizations="authorizationDiff.unchanged"
      :loading-save="loadingSave"
      @save="finalizeSave"
    />

    <shift-overlap-modal
      :visible.sync="visibleOverlapModal"
      :overlapping-shifts="overlappingShifts"
      :loading="loadingCheck"
      @reload="checkConflict"
    />

    <div v-if="loadingShift" class="loading-spin" style="height: 650px">
      <a-spin />
    </div>
    <div v-else class="wrapper">
      <div class="column">
        <div v-if="isNew" class="associate-wrapper">
          <search-associates :disabled="multiply && !!initialValues.associateNumber" :value.sync="associate_number" />
          <a-button icon="plus" @click="visibleCreateAssociateModal = true">Add New</a-button>
        </div>

        <template v-if="associate_number">
          <shift-fields
            ref="fieldsRef"
            :is-new="isNew"
            :associate_number="associate_number"
            :isLunchWaiver="isLunchWaiver"
            :disableScheduledAsSigned="disableScheduledAsSigned"
            :valid.sync="isValid"
          />
          <a-alert
            v-if="payPeriodValidationMessage"
            type="warning"
            show-icon
            message="Pay period changed"
            :description="payPeriodValidationMessage"
          />
          <div class="column">
            <div class="flex justify-content-between">
              <h3 class="m-0">Manual CDPH 530</h3>
              <div class="flex gap-2" v-if="baseUrl">
                <a-button @click="reloadAttachments" type="primary" icon="retweet" ghost></a-button>
                <a-button @click="openQrModal" type="primary" icon="plus" ghost>Add via Cell Phone</a-button>
              </div>
            </div>

            <shift-attachments :files="files" :with-title="false" @save="saveAttachmentsIds" />
          </div>

          <div v-if="!multiply" class="column">
            <h4 class="m-0">How would you like to Sign this Shift?</h4>
            <a-radio-group class="flex flex-column gap-1 p-0" v-model="signatureType">
              <a-radio class="block" value="admin">Sign with <b>MY</b> signature</a-radio>
              <a-radio class="block" value="associate"> <b>SEND TO ASSOCIATE</b> for signature </a-radio>
            </a-radio-group>

            <div v-if="signatureType === 'admin'" class="column">
              <label for="shift-signing-explanation">Explanation (required)</label>
              <a-textarea
                id="shift-signing-explanation"
                v-model="explanation"
                :max-length="2048"
                :auto-size="{ minRows: 3, maxRows: 6 }"
                aria-required="true"
                data-testid="shifts-signing-explanation"
              />
            </div>

            <signature
              v-if="signatureType === 'admin'"
              ref="signatureRef"
              checkbox-label="I agree that all data is true and correct"
              @checked="setSignatureCheckboxValue"
              @save-signature="setSignatureId"
            />
          </div>
        </template>
      </div>
    </div>
    <template slot="footer">
      <div class="flex justify-content-end">
        <a-button icon="reload" type="primary" @click="handleReset">Reset</a-button>
        <a-button
          icon="save"
          type="primary"
          :disabled="!associate_number"
          :loading="loadingAutofill"
          @click="handleAutofill"
          >Autofill</a-button
        >
        <a-button
          icon="save"
          type="primary"
          :disabled="!disableSaveButton || loadingAutofill"
          ghost
          :loading="loadingSave || loadingAuthorizationCheck"
          @click="openConfirmationModal"
          >Save</a-button
        >
      </div>
    </template>
  </a-modal>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { Alert, Button, Input, Modal, notification, Radio, Spin } from 'ant-design-vue'
import shiftFormFieldsVue from './shift-form-fields.vue'
import { SHIFT_FORM_INITIAL_STATE } from '@/store/modules/shifts/constants'
import shiftConfirmAdjustVue from './shift-confirm-adjust.vue'
import validation from '@/modules/shifts/mixins/ShiftValidationMixin'
import ShiftFormAttachments from '../../../../components/form-attachments.vue'
import ShiftOverlapModal from '../modal/shift-overlap-modal.vue'
import SearchAssociates from '@/components/search-associates.vue'
import moment from 'moment-timezone'
import QrCodeGenerator from '@/components/qr-code-generator.vue'
import shiftsHelper from '@/helpers/shifts'
import ShiftFormAdditional from '../shift-form-additional.vue'
import { deepCopy } from '@/utils/deep-copy'
import ProfileCreateAssociateModal from '@/modules/profile/components/profile-create-associate-modal.vue'
import Signature from '@/components/signature.vue'
import util from '@/util'

const { mapActions: shiftActions, mapState: shiftState } = createNamespacedHelpers('shifts')
const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: userActions } = createNamespacedHelpers('user')
const { mapActions: documentActions } = createNamespacedHelpers('document')
const { mapActions: reportActions } = createNamespacedHelpers('report')

export default {
  components: {
    'a-button': Button,
    'a-alert': Alert,
    'a-textarea': Input.TextArea,
    'a-radio-group': Radio.Group,
    'a-radio': Radio,
    'a-spin': Spin,
    'a-modal': Modal,
    'associate-create-modal': ProfileCreateAssociateModal,
    'shift-fields': shiftFormFieldsVue,
    'shift-confirmation': shiftConfirmAdjustVue,
    'shift-attachments': ShiftFormAttachments,
    'shift-overlap-modal': ShiftOverlapModal,
    'search-associates': SearchAssociates,
    'qr-code-generator': QrCodeGenerator,
    'shift-additional': ShiftFormAdditional,
    signature: Signature
  },
  mixins: [validation, shiftsHelper],
  props: {
    shiftId: {
      type: Number,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    },
    edit: {
      type: Function
    },
    multiply: {
      type: Boolean,
      default: false
    },
    initialValues: {
      type: Object
    },
    disableScheduledAsSigned: {
      type: Boolean,
      default: false
    },
    validatePbjPayPeriod: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loadingShift: false,
      loadingSave: false,
      loadingAutofill: false,
      loadingCheck: false,
      loadingAuthorizationCheck: false,

      shift_id: undefined,
      associate_number: undefined,
      attachmentsIds: [],
      attachmentsDeleted: new Set(),
      overlappingShifts: [],

      visibleCreateAssociateModal: false,
      visibleConfirmationModal: false,
      visibleOverlapModal: false,
      visibleQrModal: false,

      baseUrl: undefined,

      isLunchWaiver: false,
      isValid: true,

      originalPayPeriods: [],

      user: undefined,
      signatureCheckboxChecked: false,
      signatureId: undefined,
      signatureType: 'associate',
      explanation: '',
      pendingAuthorizationNames: [],
      authorizationDiff: {
        added: [],
        removed: [],
        unchanged: []
      }
    }
  },
  watch: {
    signatureType() {
      this.explanation = ''
      this.signatureId = undefined
      this.signatureCheckboxChecked = false
      if (this.signatureType === 'admin') {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            const signatureComponent = this.$refs.signatureRef
            const signatureElement = signatureComponent?.$el ?? signatureComponent

            if (signatureElement?.scrollIntoView) {
              signatureElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            }
          })
        })
      }
    },
    associate_number: {
      handler(newVal) {
        this.resetSignatureData()
        if (newVal) this.fetchAssociate()
      }
    },
    visible: {
      handler(newVal) {
        if (newVal) {
          this.processInitialValues()
          this.setFormValues()
          this.fetchOriginalPayPeriods()
          this.attachmentsIds = []
          this.attachmentsDeleted = new Set()
          this.overlappingShifts = []
          this.resetSignatureData()
          this.clearAuthorizationDiff()
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (!this.shift) return

    if (this.shift?.associate?.id && this.shift?.shift?.id) {
      const origin = window.location.origin
      const path = origin.includes('localhost') ? 'https://qa.epeople.com/' : `${origin}`

      this.baseUrl = `${path}/whereami/#/attach-files?associate_id=${
        this.shift.associate.id
      }&shift_id=${this.shift.shift.id}&created_at=${moment().utc().seconds(0).format()}`
    }
  },
  computed: {
    ...shiftState({
      shift: state => state.shift,
      shiftForm: state => state.shiftForm
    }),

    ...profileState({
      associate: state => state.associate
    }),

    files() {
      return this.isNew ? [] : this.shift.shift.files
    },

    isNew() {
      return !this.shiftId
    },

    disableSaveButton() {
      const hasRequiredFields = this.shiftForm.work_end && this.shiftForm.work_start && this.associate_number

      if (!hasRequiredFields || !this.hasValidTimeFields) {
        return false
      }

      if (this.multiply) {
        return true
      }

      return this.signatureType !== 'admin' || (this.signatureCheckboxChecked && !this.signingExplanationError)
    },

    signingExplanation() {
      return this.signatureType === 'admin' && !this.multiply ? this.explanation.trim() : undefined
    },

    signingExplanationError() {
      if (this.signatureType !== 'admin' || this.multiply) return null
      if (!this.signingExplanation) return 'Please explain why you changed this shift and signed it yourself.'
      if (this.signingExplanation.length > 2048) return 'Explanation must be 2048 characters or fewer.'
      return null
    },

    hasValidTimeFields() {
      return [
        this.validateWorkStart,
        this.validateWorkEnd,
        this.validateFirstLunchStart,
        this.validateFirstLunchEnd,
        this.validateSecondLunchStart,
        this.validateSecondLunchEnd
      ].every(message => message === null)
    },

    isAssociateDisabled() {
      return this.associate?.work_status === 'disabled'
    },

    associateFullName() {
      if (!this.associate) {
        return this.associate_number || ''
      }

      return util.combineAssociateName(this.associate.first_name, this.associate.last_name)
    },

    signedByName() {
      if (this.signatureType === 'admin') {
        return 'MY signature'
      }

      if (this.signatureType === 'associate') {
        return `${this.associateFullName} (request will be sent)`
      }

      return this.associateFullName
    },

    title() {
      return this.isNew ? 'Create Shift' : 'Adjust Shift'
    },

    originalPayPeriod() {
      const originalStart = this.shift?.shift?.work_start

      if (!originalStart) {
        return this.originalPayPeriods[0]
      }

      return (
        this.originalPayPeriods.find(period =>
          moment(originalStart).isBetween(
            moment(period.start).startOf('day'),
            moment(period.end).endOf('day'),
            null,
            '[]'
          )
        ) ?? this.originalPayPeriods[0]
      )
    },

    payPeriodValidationMessage() {
      if (!this.validatePbjPayPeriod || this.isNew || !this.originalPayPeriod || !this.shiftForm.work_start) {
        return undefined
      }

      const selectedStart = moment(this.shiftForm.work_start)
      const periodStart = moment(this.originalPayPeriod.start).startOf('day')
      const periodEnd = moment(this.originalPayPeriod.end).endOf('day')

      if (selectedStart.isBetween(periodStart, periodEnd, null, '[]')) {
        return undefined
      }

      return `The start date you selected moves this shift outside of Pay Period (${periodStart.format(
        'MM/DD/YYYY'
      )} - ${periodEnd.format('MM/DD/YYYY')}). Saving will reassign the shift to a different pay period.`
    }
  },
  methods: {
    ...shiftActions([
      'setShiftFields',
      'updateShift',
      'createShift',
      'postShiftConflict',
      'getShiftAttachments',
      'postAuthorizationCheck',
      'updateShiftField'
    ]),
    ...profileActions(['getAssociate']),
    ...userActions(['getUser']),
    ...documentActions(['getDocumentByType']),
    ...reportActions(['validatePayPeriod']),

    openQrModal() {
      this.visibleQrModal = true
    },

    closeQrModal() {
      this.visibleQrModal = false
    },

    async reloadAttachments() {
      try {
        const payload = {
          id: this.shift.shift.id
        }

        await this.getShiftAttachments(payload)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    formatDateForPayPeriodValidation(date) {
      return moment(date).format(this.$store.state.applicationState.serverDateFormat)
    },

    async fetchOriginalPayPeriods() {
      if (!this.validatePbjPayPeriod || this.isNew || !this.shift?.shift?.work_start || !this.shift?.shift?.work_end) {
        this.originalPayPeriods = []
        return
      }

      try {
        this.originalPayPeriods = await this.validatePayPeriod({
          start: this.formatDateForPayPeriodValidation(this.shift.shift.work_start),
          end: this.formatDateForPayPeriodValidation(this.shift.shift.work_end)
        })
      } catch (error) {
        this.originalPayPeriods = []
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    processInitialValues() {
      if (!this.initialValues || this.shiftId) return

      if (this.initialValues.associateNumber) {
        this.associate_number = this.initialValues.associateNumber
      }

      if (this.initialValues.start) {
        this.shiftForm.work_start = this.resetSeconds(
          moment(this.initialValues.start).utc(true).tz(this.timeZone).startOf('day').add(510, 'minutes')
        )

        this.shiftForm.work_end = this.workEndInitialTime
      }
    },

    closeModal() {
      this.setShiftFields(SHIFT_FORM_INITIAL_STATE)
      this.associate_number = undefined
      this.$emit('update:visible', false)
    },

    setFormValues() {
      if (!this.isNew && Object.keys(this.shift).length > 0) {
        this.associate_number = this.shift.associate.associate_number
        const formattedFields = this.formatTimeFields({
          ...this.shift.shift
        })

        if (this.$refs.fieldsRef) {
          this.$refs.fieldsRef.setChangeByAutofill(true)
        }

        this.setShiftFields({
          ...formattedFields,
          set_scheduled_as_signed: this.disableScheduledAsSigned ? true : formattedFields.set_scheduled_as_signed
        })
      }
    },

    async handleAutofill() {
      this.loadingAutofill = true
      await this.$refs.fieldsRef.fillWorkTimes()
      this.loadingAutofill = false
    },

    handleReset() {
      this.resetSignatureData()
      if (!this.isNew) {
        this.setFormValues()
      } else {
        this.setShiftFields(SHIFT_FORM_INITIAL_STATE)
      }

      this.$refs.fieldsRef.validateFields()
    },

    afterCreateAssociate(associate) {
      this.associate_number = associate.associate_number
    },

    saveAttachmentsIds(ids, flag, deleted_id) {
      this.attachmentsIds = ids

      if (flag === 'remove') this.attachmentsDeleted.add(deleted_id)
    },

    resetSignatureData() {
      this.explanation = ''
      this.signatureCheckboxChecked = false
      this.signatureId = undefined
      this.signatureType = 'associate'
    },

    setSignatureCheckboxValue(e) {
      this.signatureCheckboxChecked = e
    },

    setSignatureId(signatureId) {
      this.signatureId = signatureId
    },

    clearAuthorizationDiff() {
      this.pendingAuthorizationNames = []
      this.authorizationDiff = {
        added: [],
        removed: [],
        unchanged: []
      }
    },

    normalizeAuthorizationName(authorization) {
      if (!authorization) return undefined
      if (typeof authorization === 'string') return authorization

      return authorization.key ?? authorization.type ?? authorization.name
    },

    normalizeAuthorizations(authorizations = []) {
      const names = authorizations.map(this.normalizeAuthorizationName).filter(Boolean)

      return [...new Set(names)]
    },

    getPersistedUnresolvedAuthorizations() {
      const source = this.shift?.shift?.authorizations_with_status ?? this.shift?.shift?.authorizations ?? []
      const authorizations = Array.isArray(source) ? source : []

      return authorizations.filter(authorization => {
        if (!authorization || typeof authorization === 'string') return true
        if (authorization.deleted_at) return false

        return !authorization.status || authorization.status === 'initial'
      })
    },

    formatAuthorizationCheckDate(date) {
      return date ? moment(date).utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : null
    },

    getAuthorizationCheckShiftPayload(shift) {
      return {
        scheduled_start: this.formatAuthorizationCheckDate(shift?.scheduled_start),
        scheduled_end: this.formatAuthorizationCheckDate(shift?.scheduled_end),
        work_start: this.formatAuthorizationCheckDate(shift?.work_start),
        work_end: this.formatAuthorizationCheckDate(shift?.work_end),
        lunch_start: this.formatAuthorizationCheckDate(shift?.lunch_start),
        lunch_end: this.formatAuthorizationCheckDate(shift?.lunch_end),
        second_lunch_start: this.formatAuthorizationCheckDate(shift?.second_lunch_start),
        second_lunch_end: this.formatAuthorizationCheckDate(shift?.second_lunch_end),
        rest_break_checked: shift?.rest_break_checked === null ? false : (shift?.rest_break_checked ?? true),
        lunch_waived: !!shift?.lunch_waived,
        agree_with_first_lunch_period: shift?.agree_with_first_lunch_period ?? true
      }
    },

    buildAuthorizationDiff(currentAuthorizations, nextAuthorizations) {
      const currentNames = this.normalizeAuthorizations([
        ...this.normalizeAuthorizations(currentAuthorizations),
        ...this.normalizeAuthorizations(this.getPersistedUnresolvedAuthorizations())
      ])
      const nextNames = this.normalizeAuthorizations(nextAuthorizations)
      const currentSet = new Set(currentNames)
      const nextSet = new Set(nextNames)

      this.pendingAuthorizationNames = nextNames
      this.authorizationDiff = {
        added: nextNames.filter(name => !currentSet.has(name)).map(name => ({ name })),
        removed: currentNames.filter(name => !nextSet.has(name)).map(name => ({ name })),
        unchanged: currentNames.filter(name => nextSet.has(name)).map(name => ({ name }))
      }

      return this.authorizationDiff
    },

    hasAuthorizationReviewOutcome(diff) {
      return diff.added.length > 0 || diff.removed.length > 0 || diff.unchanged.length > 0
    },

    applySaveDetails() {
      this.updateShiftField({
        field: 'signature',
        value: this.signatureId ? { id: this.signatureId } : undefined
      })

      this.updateShiftField({
        field: 'authorizations',
        value: this.pendingAuthorizationNames
      })
    },

    confirmEmployeeAdminSignature() {
      if (this.associate?.payment_info?.type !== 'Employee' || this.signatureType !== 'admin') {
        return Promise.resolve(true)
      }

      return new Promise(resolve => {
        Modal.confirm({
          title: 'Are you sure?',
          content:
            "Are you sure you want to sign this shift on behalf of the EMPLOYEE? This could lead to legal issues down the line. It's best to only do this in rare, exceptional cases.",
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        })
      })
    },

    async prepareSignatureForSave() {
      this.signatureId = undefined

      if (this.signatureType !== 'admin') {
        return true
      }

      await this.$refs.signatureRef.saveSignature()

      return !!this.signatureId
    },

    confirmDeactivatedAssociateShift() {
      return new Promise(resolve => {
        Modal.confirm({
          title: 'Add a shift for a deactivated person?',
          content:
            'Are you sure you want to add a shift for someone who hasn’t worked here in a long time? Please double-check that everything is correct!',
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        })
      })
    },

    async openConfirmationModal() {
      if (this.signingExplanationError) {
        this.showNotification('warning', 'Explanation is required', this.signingExplanationError)
        return
      }
      await this.$refs.fieldsRef.validateForm()

      if (this.isValid) {
        if (this.isNew && this.isAssociateDisabled) {
          const confirmed = await this.confirmDeactivatedAssociateShift()

          if (!confirmed) return
        }

        if (this.multiply) {
          this.create(false)

          return
        }

        await this.checkAuthorizationChanges()
      }
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    async checkAuthorizationChanges() {
      this.loadingAuthorizationCheck = true

      try {
        const initialShift = this.isNew ? undefined : this.getAuthorizationCheckShiftPayload(this.shift?.shift)
        const currentShift = this.getAuthorizationCheckShiftPayload(this.shiftForm)
        const currentAuthorizations = initialShift
          ? await this.postAuthorizationCheck({
              associate_number: this.associate_number,
              shift: initialShift
            })
          : []
        const nextAuthorizations = await this.postAuthorizationCheck({
          associate_number: this.associate_number,
          shift: currentShift
        })
        const diff = this.buildAuthorizationDiff(currentAuthorizations, nextAuthorizations)

        if (!this.hasAuthorizationReviewOutcome(diff)) {
          await this.finalizeSave()
          return
        }

        this.visibleConfirmationModal = true
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingAuthorizationCheck = false
      }
    },

    async finalizeSave() {
      if (this.signingExplanationError) {
        this.showNotification('warning', 'Explanation is required', this.signingExplanationError)
        return
      }
      const confirmed = await this.confirmEmployeeAdminSignature()

      if (!confirmed) return

      const signaturePrepared = await this.prepareSignatureForSave()

      if (!signaturePrepared) return

      this.applySaveDetails()
      this.visibleConfirmationModal = false
      await this.saveShift(false)
      this.clearAuthorizationDiff()
    },

    openOverlapShifts(shifts) {
      this.visibleOverlapModal = true
      this.overlappingShifts = deepCopy(shifts)
    },

    // async calls

    async fetchAssociate() {
      this.loadingShift = true

      try {
        const payload = {
          associate_number: this.associate_number
        }

        if (!this.associate_number) {
          return
        }

        await this.getAssociate(payload)

        this.user = await this.getUser({ id: this.associate.user_id })

        this.$emit('fetchUser', {
          user: this.user,
          isAssociateDisabled: this.isAssociateDisabled
        })

        const data = await this.getDocumentByType({
          associate_id: this.associate.id,
          type: 'lunch_waiver'
        })

        this.isLunchWaiver = data.signature !== null
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingShift = false
      }
    },

    async saveShift() {
      this.isNew ? this.create() : this.update()
    },

    async create() {
      this.loadingSave = true

      try {
        const shift = {
          ...this.formatTimeFields(this.shiftForm),
          explanation: this.signingExplanation,
          modified_by_admin: true,
          rest_break_checked: true,
          files: [...this.attachmentsIds]
        }

        const createPayload = {
          associate_number: this.associate_number,
          auto_sign: false,
          shift
        }

        if (this.multiply) {
          this.closeModal()
          this.$emit('edit', shift, this.associate_number)

          return
        }

        await this.checkConflict()

        if (this.overlappingShifts.length) return

        await this.createShift(createPayload)

        this.showNotification('success', 'Success', 'Associate Shift Saved')

        this.closeModal()
        this.$emit('edit')
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingSave = false
      }
    },

    async checkConflict() {
      this.loadingCheck = true
      try {
        const data = await this.postShiftConflict({
          associate_number: this.associate_number
        })

        if (data.length > 0) {
          const shifts = data.map(item => item.shifts).flat()
          this.openOverlapShifts(shifts)
        } else {
          this.overlappingShifts = []
        }
      } catch (error) {
        return Promise.reject(error)
      } finally {
        this.loadingCheck = false
      }
    },

    async update() {
      this.loadingSave = true

      try {
        const formattedFields = this.formatTimeFields(this.shiftForm)

        await this.checkConflict()

        if (this.overlappingShifts.length) return

        await this.getShiftAttachments({
          id: this.shift.shift.id
        })

        const oldFiles = this.shift.shift.files.map(item => item.id)
        const newFiles = this.attachmentsIds.map(item => item.id)
        const uniqueFiles = Array.from(new Set([...oldFiles, ...newFiles]))

        const updatePayload = {
          associate_number: this.associate_number,
          shift: {
            ...formattedFields,
            explanation: this.signingExplanation,
            rest_break_checked: this.shiftForm.rest_break_checked === null ? false : this.shiftForm.rest_break_checked,
            modified_by_admin: true,
            files: uniqueFiles.filter(item => !this.attachmentsDeleted.has(item)).map(item => ({ id: item }))
          },
          auto_sign: false
        }

        await this.updateShift(updatePayload)

        this.showNotification('success', 'Success', 'Associate Shift Saved')

        this.closeModal()
        this.$emit('edit')
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingSave = false
      }
    }
  }
}
</script>

<style scoped>
::v-deep .ant-modal-body {
  max-height: 900px;
  overflow: auto;
}

.wrapper {
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr;
}

.column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.associate-wrapper,
.attachments-wrapper {
  display: grid;
  gap: 16px;
  grid-template-columns: 2fr 1fr;
}

::v-deep .ant-descriptions-view {
  min-width: 0;
}
</style>
